/* eslint-disable react/jsx-one-expression-per-line */
import * as React from "react";
import { Link } from "react-router-dom";
import Collapsible from "react-collapsible";

// Components
import Container from "../components/Container/Container";
import Footer from "../components/Footer/Footer";
import Button from "../components/Button/Button";

const HowItWorksPage: React.FC = () => {
  return (
    <>
      <Container>
        <div className="w-11/12 xxl:w-full mx-auto pb-4">
          <h1 className="text-xl md:text-38 lg:text-46 text-dw-blue font-semibold font-serif mb-2">
            How Do I Use Threat Check?
          </h1>
          <p className="font-bold text-dw-grey-13 mb-4 md:text-18">
            You can do many different things with Threat Check. We listed the
            most frequently asked questions below to explain you how to use
            Threat Check.
          </p>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Do I have to register an account with Threat Check?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                No. You never have to register with Threat Check using any
                personal information like your name, email address or phone
                number. Threat Check is free to use and can be used without an
                account. Only you decide what information you would like to
                provide to Threat Check.{" "}
                <Link to="/faq/security" className="font-bold underline">
                  See here
                </Link>{" "}
                how Threat Check uses your data to provide your results.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="What can I expect from Threat Check? What is the end result?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                You will get two results: Firstly, a complete, individual
                digital security policy based off the answers you submitted.
                This is a long version and will be approximately three to six
                pages long per topic. Secondly, a poster with the most important
                parts of the policy on one page. Both documents will be PDFs.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="In total there are policies available for eight different topics.
              Do I have to fill them all out to get my results?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                No. After finishing the “Must Haves” for a certain topic, you
                can click on “Get Results” and “Finish Policy” to download your
                results.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Which topics do you recommend filling out?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                We don’t know your security needs, therefore we cannot say which
                topics are more important for your digital security. If, for
                example, you are especially worried about travel, our topic
                “Safe travelling” is probably very relevant for you. Threat
                Check aims to be flexible enough so that you only have to fill
                out what you find important for your work.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="I have to fill in a statement, but I don’t want to answer it. Can
              I get the results without?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                If your statement is considered to be a “Must Have”, you have to
                answer all statements to get your results. We believe that these
                “Must Haves” should be the bare minimum of every digital
                security concept. Statements under “Should Haves” are
                recommended, but you also get a result without filling them out
                completely.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="It says, “this field is required”, but I don’t want to answer it.
              What can I do?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                If a field is required, you have to answer it in order to
                complete that part of the policy. Without that field, you won’t
                be able to finish a policy and therefore you won’t get your
                results.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="What is the difference between “Must Haves” and “Should Haves”?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                All statements under “Must Haves” have to be answered to get
                your individual digital security policy. We believe that these
                “Must Haves” should be the bare minimum of every digital
                security concept. For example, if you want to have a security
                policy for securing your accounts, you have to answer how you
                want to handle your passwords and how you want to use
                Two-Factor-Authentication. Statements under “Should Haves”,
                however, are only recommended and you still get a policy without
                filling them out completely.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="I don’t have time to answer all the questions. What can I do?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                We offer you a set of standard answers in three different modes:
                safe, safer and safest. For every question with specific
                statements, you see a Button “Need help filling out the
                policy?”. Click on the option that you would like to choose and
                then click “Apply”. However, it might still be necessary to
                answer certain statements, especially when there’s an empty text
                field and you may have to enter a name or contact address.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="How do I save my current status?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                Click under “Get Results” on{" "}
                <Link to="/save" className="font-bold underline">
                  “Save Progress”
                </Link>
                . You can download a file ending with “.threatcheck”. You can
                upload this file again later under{" "}
                <Link to="/save" className="font-bold underline">
                  “Continue Policy”
                </Link>{" "}
                and all your answers will appear again in the tool.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="I closed the browser tab. Where are my answers?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                If you didn’t save your results under “Save Progress” or “Finish
                Policy” and get a file ending with “.threatcheck”, all your
                answers have been lost. For security reasons, Threat Check
                doesn’t store any data about its users on the server.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="I want to update my policy. What do I have to do?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                If you used Threat Check already some time ago, you have to
                upload the file ending with “.threatcheck” under{" "}
                <Link to="/continue" className="font-bold underline">
                  “Continue Policy”
                </Link>
                . You could have downloaded it under{" "}
                <Link to="/save" className="font-bold underline">
                  “Save Progress”
                </Link>
                . If you don’t have this file anymore and you closed the tab
                from your previous session, you have to start again. Threat
                Check doesn’t store any data about its users on the server.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Why should I trust you? Who is behind Threat Check?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                Threat Check is a project from two German organizations{" "}
                <a
                  href="https://www.giz.de/en/html/index.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline"
                >
                  GIZ GmbH
                </a>
                (Deutsche Gesellschaft für Internationale Zusammenarbeit) and{" "}
                <a
                  href="https://www.dw.com/en/dw-akademie/about-us/s-9519"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold underline"
                >
                  DW Akademie
                </a>
                .
                <br />
                <br />
                GIZ is a federal enterprise with worldwide operations. It
                supports the German government in the field of international
                cooperation for sustainable development and is also engaged in
                international education activities in 153 countries around the
                globe. DW Akademie is Deutsche Welle’s center for international
                media development, journalism training and knowledge transfer.
                With projects in more than 50 countries worldwide, its work
                strengthens the human right to freedom of expression and
                unhindered access to information.
                <br />
                <br />
                DW Akademie is a strategic partner of the German Federal
                Ministry for Economic Cooperation and Development and also
                receives funding from the German Federal Foreign Office and the
                European Union.
              </p>
            </Collapsible>
          </div>
          <div className="mb-4 text-dw-grey-13">
            <Collapsible
              trigger="Sometimes you recommend certain software. Is software you
              recommend secure?"
              triggerClassName="text-20 font-bold mb-3 cursor-pointer"
              triggerOpenedClassName="text-20 font-bold mb-3 cursor-pointer"
            >
              <p className="mt-2">
                Complete digital security is never absolutely achievable. That’s
                why we cannot guarantee that software is 100 percent “secure”.
                We only mention software where we believed that it is either a
                good option or it is heavily used by people and banning the
                software would be artificial and non-practical. However, please
                always read our information and feedback for specific
                statements, check when the statement was “last updated” and do
                your own research on certain products independently from Threat
                Check.
              </p>
            </Collapsible>
          </div>
          <Link to="/categories">
            <Button type="button" buttonType="secondary">
              Proceed to Policy
            </Button>
          </Link>
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default HowItWorksPage;
